import React, { useState, FC } from 'react'
import Form, { FormComponentProps } from 'antd/lib/form'
import { Input, message, Button } from 'antd'
import { Reseller, ResellerFormValues } from '../../types'
import styles from './index.module.less'
import { useAuth } from '../../providers/AuthProvider'
import ServiceProviderSelect from './ServiceProviderSelect'

interface ResellerAccountFormProps extends FormComponentProps {
	reseller?: Reseller
	submit(values: ResellerFormValues): Promise<void>
	cancel?(): void
}

const ResellerAccountForm: FC<ResellerAccountFormProps> = props => {
	const [isSaving, setIsSaving] = useState<boolean>(false)
	const { user, userHasPermission, userHasManyServiceProviders } = useAuth()
	const hasReseller = typeof props.reseller !== 'undefined'
	const userIsMondagoAdmin = userHasPermission(['ServiceProvider.All.ReadWrite'])

	const onSubmit = (): void => {
		setIsSaving(true)
		props.form.validateFields(
			async (errors, values): Promise<void> => {
				if (errors) {
					setIsSaving(false)
					message.error(errors)
					return
				}

				await props.submit(values)
				setIsSaving(false)
			}
		)
	}

	return (
		<Form className={styles.form} hideRequiredMark layout='horizontal' labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign='left'>
			{!hasReseller ? (
				userIsMondagoAdmin || userHasManyServiceProviders ? (
					<ServiceProviderSelect form={props.form} />
				) : (
					props.form.getFieldDecorator('service_provider_id', { initialValue: user.service_providers[0].id })(<Input hidden />)
				)
			) : (
				props.form.getFieldDecorator('service_provider_id', { initialValue: props.reseller.service_provider_id })(<Input hidden />)
			)}
			<Form.Item className={styles.formItem} label='Name'>
				{props.form.getFieldDecorator('name', {
					rules: [
						{
							whitespace: true,
							required: true,
							message: 'Name is required.'
						}
					],
					initialValue: hasReseller ? props.reseller.name : ''
				})(<Input placeholder='Enter Name' type='text' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} hasFeedback label='Email'>
				{props.form.getFieldDecorator('email', {
					rules: [
						{
							type: 'email',
							whitespace: true,
							required: false
						}
					],
					initialValue: hasReseller ? props.reseller.email : ''
				})(<Input spellCheck={false} placeholder='Enter Email' type='text' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Phone number'>
				{props.form.getFieldDecorator('phone_number', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasReseller ? props.reseller.phone_number : ''
				})(<Input placeholder='Enter Phone Number' type='text' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Address 1'>
				{props.form.getFieldDecorator('address1', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasReseller ? props.reseller.address1 : ''
				})(<Input placeholder='Enter Address 1' type='text' />)}
			</Form.Item>
			<Form.Item className={styles.formItem} label='Address 2'>
				{props.form.getFieldDecorator('address2', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasReseller ? props.reseller.address2 : ''
				})(<Input placeholder='Enter Address 2' type='text' />)}
			</Form.Item>
			<Form.Item label='Postcode' className={styles.formItem}>
				{props.form.getFieldDecorator('postcode', {
					rules: [
						{
							whitespace: true,
							required: false
						}
					],
					initialValue: hasReseller ? props.reseller.postcode : ''
				})(
					<Input
						spellCheck={false}
						onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
							e.target.value = e.target.value.toUpperCase()
						}}
						placeholder='Enter Postcode'
						type='text'
					/>
				)}
			</Form.Item>
			{userIsMondagoAdmin && (
				<Form.Item label='Distributor' className={styles.formItem}>
					{props.form.getFieldDecorator('distributor', {
						rules: [
							{
								whitespace: true,
								required: false
							}
						],
						initialValue: hasReseller ? props.reseller.distributor : ''
					})(<Input spellCheck={false} placeholder='Enter Distributor' type='text' />)}
				</Form.Item>
			)}
			<div className={styles.buttons}>
				{props.cancel && (
					<Button className={styles.cancelButton} onClick={props.cancel}>
						Cancel
					</Button>
				)}
				<Button loading={isSaving} type='primary' onClick={onSubmit}>
					{isSaving ? 'Saving' : 'Save'}
				</Button>
			</div>
		</Form>
	)
}

export default Form.create<ResellerAccountFormProps>()(ResellerAccountForm)
